import { DollarSign, TrendingUp, TrendingDown, Clock, AlertTriangle } from 'lucide-react';
import clsx from 'clsx';

interface LaborCostPanelProps {
  totalCostCents: number;
  budgetCents?: number;
  totalHours?: number;
  employeeCount?: number;
  /** Number of scheduled employees with no pay rate set (their hours aren't costed). */
  missingRateCount?: number;
}

export default function LaborCostPanel({
  totalCostCents,
  budgetCents,
  totalHours,
  employeeCount,
  missingRateCount,
}: LaborCostPanelProps) {
  const totalDollars = totalCostCents / 100;
  const budgetDollars = budgetCents ? budgetCents / 100 : null;

  // Calculate percentage of budget
  const percentOfBudget = budgetCents ? (totalCostCents / budgetCents) * 100 : null;
  const isOverBudget = percentOfBudget !== null && percentOfBudget > 100;
  const isNearBudget = percentOfBudget !== null && percentOfBudget >= 90 && percentOfBudget <= 100;

  return (
    <div className="bg-white border-t border-slate-200 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Labor Cost */}
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <div className="text-xs text-slate-500">Labor Cost</div>
              <div className="text-lg font-semibold text-slate-900">
                ${totalDollars.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </div>
            </div>
          </div>

          {/* Incomplete-data warning: some scheduled staff have no pay rate */}
          {missingRateCount !== undefined && missingRateCount > 0 && (
            <div
              className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-amber-50 text-amber-700 text-xs font-medium"
              title="These employees have no hourly rate set, so their hours aren't included in the labor cost."
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              Understated · {missingRateCount} missing pay rate{missingRateCount === 1 ? '' : 's'}
            </div>
          )}

          {/* Budget comparison */}
          {budgetDollars !== null && (
            <div className="flex items-center gap-2">
              <div className="text-sm text-slate-500">
                / ${budgetDollars.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} budget
              </div>
              <div
                className={clsx(
                  'flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                  isOverBudget && 'bg-red-100 text-red-700',
                  isNearBudget && 'bg-amber-100 text-amber-700',
                  !isOverBudget && !isNearBudget && 'bg-emerald-100 text-emerald-700'
                )}
              >
                {isOverBudget ? (
                  <>
                    <TrendingUp className="w-3 h-3" />
                    {percentOfBudget?.toFixed(0)}%
                  </>
                ) : (
                  <>
                    {percentOfBudget !== null && percentOfBudget < 90 && <TrendingDown className="w-3 h-3" />}
                    {percentOfBudget?.toFixed(0)}%
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 text-sm">
          {totalHours !== undefined && (
            <div className="flex items-center gap-1.5 text-slate-600">
              <Clock className="w-4 h-4" />
              <span>{totalHours.toFixed(1)} hours</span>
            </div>
          )}

          {employeeCount !== undefined && (
            <div className="text-slate-600">
              <span className="font-medium">{employeeCount}</span> employees scheduled
            </div>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {budgetCents && (
        <div className="mt-3">
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={clsx(
                'h-full rounded-full transition-all',
                isOverBudget && 'bg-red-500',
                isNearBudget && 'bg-amber-500',
                !isOverBudget && !isNearBudget && 'bg-emerald-500'
              )}
              style={{ width: `${Math.min(percentOfBudget || 0, 100)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
